import { Globe } from 'lucide-react';

/** What an Ask answer searched for (`webSearches` on the server's `complete` frame). */
export interface WebSearchEntry {
  query: string;
  resultsUsed?: number;
}

/** A web source the answer cites (`citations` on the server's `complete` frame). */
export interface WebCitation {
  url: string;
  title?: string;
}

export interface WebActivity {
  webSearches?: WebSearchEntry[];
  citations?: WebCitation[];
}

const MAX_SOURCES = 8;

function safeHttpUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.toString() : null;
  } catch {
    return null;
  }
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/**
 * Reads untrusted frame/DB data into a clean `WebActivity`, or `undefined` when
 * the answer did not touch the web. Only http(s) links survive.
 */
export function normalizeWebActivity(raw: unknown): WebActivity | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const source = raw as { webSearches?: unknown; citations?: unknown };

  const webSearches: WebSearchEntry[] = Array.isArray(source.webSearches)
    ? source.webSearches.flatMap((entry) => {
        const query = typeof (entry as WebSearchEntry)?.query === 'string'
          ? (entry as WebSearchEntry).query.trim()
          : '';
        if (!query) return [];
        const resultsUsed = Number((entry as WebSearchEntry).resultsUsed);
        return [{ query, ...(Number.isFinite(resultsUsed) ? { resultsUsed } : {}) }];
      })
    : [];

  const seen = new Set<string>();
  const citations: WebCitation[] = Array.isArray(source.citations)
    ? source.citations.flatMap((entry) => {
        const url = safeHttpUrl((entry as WebCitation)?.url);
        if (!url || seen.has(url)) return [];
        seen.add(url);
        const title = typeof (entry as WebCitation).title === 'string'
          ? (entry as WebCitation).title!.trim()
          : '';
        return [{ url, ...(title ? { title } : {}) }];
      }).slice(0, MAX_SOURCES)
    : [];

  if (!webSearches.length && !citations.length) return undefined;
  return {
    ...(webSearches.length ? { webSearches } : {}),
    ...(citations.length ? { citations } : {}),
  };
}

/** "Searched the web for: …" plus the source links, under an Ask answer. Renders nothing without web activity. */
export function WebSources({ webSearches, citations }: WebActivity) {
  const activity = normalizeWebActivity({ webSearches, citations });
  if (!activity) return null;
  const queries = activity.webSearches ?? [];
  const sources = activity.citations ?? [];

  return (
    <div
      data-testid="web-sources"
      className="mt-3 border-t border-how-accent-warm/20 pt-3 text-xs leading-5 text-how-text-secondary"
    >
      <p className="flex items-start gap-1.5">
        <Globe aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0 text-how-accent-warm" />
        {queries.length ? (
          <span>
            Searched the web for:{' '}
            {queries.map((entry, index) => (
              <span key={`${entry.query}-${index}`}>
                {index > 0 && ', '}
                <span className="italic">“{entry.query}”</span>
              </span>
            ))}
          </span>
        ) : (
          <span>Read from the web</span>
        )}
      </p>
      {sources.length > 0 && (
        <ol aria-label="Web sources" className="mt-1.5 space-y-1 pl-5">
          {sources.map((source, index) => (
            <li key={source.url} className="flex gap-1.5">
              <span aria-hidden="true" className="tabular-nums">{index + 1}.</span>
              <a
                href={source.url}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="text-how-space-deep underline decoration-how-accent-warm/60 underline-offset-2 hover:decoration-how-accent-warm"
              >
                {source.title || hostOf(source.url)}
              </a>
              <span className="truncate opacity-70">{hostOf(source.url)}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
